import * as React from "react"

import { Input } from "@/components/ui/input"
import { formatDateLegible } from "@/lib/format"
import { useI18n } from "@/lib/i18n"
import { cn } from "@/lib/utils"

/**
 * Un campo de fecha que NO se puede leer al reves.
 *
 * EL PROBLEMA. `<input type="date">` guarda ISO —el contrato con el backend
 * nunca es ambiguo— pero se PINTA con el formato del navegador, no con el de la
 * aplicacion. El mismo campo muestra «01/07/2026» en un navegador en español y
 * «07/01/2026» en uno en ingles, sobre la misma pantalla en español. Las dos
 * cadenas son fechas validas y distintas: 1 de julio y 7 de enero.
 *
 * Es el mismo defecto que `SelectorDePeriodo` resolvio para el mes —ahi se veia
 * «August 2026» y saltaba a la vista— en su version SILENCIOSA: aqui no hay
 * nada raro que mirar. El estado contable sale de otro rango, cuadra igual, y
 * quien lo firma creyo estar mirando julio.
 *
 * POR QUE NO SE SUSTITUYE EL NATIVO. Tres `select` para dia, mes y año se
 * operan peor que un calendario, y una fecha de corte se teclea. El nativo se
 * queda con su teclado y su selector; lo que se añade es un ECO con el mes por
 * su NOMBRE, que no se puede leer al reves en ninguna configuracion. Cuesta una
 * linea de 11px y quita la ambiguedad entera.
 *
 * El eco sigue el idioma de la SOCIEDAD, como todo lo demas del producto: «1
 * jul 2026» en Peru, «1 de jul. de 2026» en Mexico, «1 de jul. de 2026» en
 * Brasil segun su locale. No sigue al navegador — esa es justamente la regla
 * que el campo nativo rompe.
 */
export function SelectorDeFecha({
  value,
  onChange,
  id,
  className,
  disabled,
  min,
  max,
  "aria-label": ariaLabel,
}: {
  /** ISO 'YYYY-MM-DD'. El contrato con el backend no cambia. */
  value: string
  onChange: (v: string) => void
  id?: string
  className?: string
  disabled?: boolean
  min?: string
  max?: string
  "aria-label"?: string
}) {
  // `useI18n()` no se usa para leer el locale —`formatDateLegible` ya toma el
  // activo que fija `I18nProvider`— sino para SUSCRIBIRSE: sin esto, cambiar de
  // sociedad no repinta el eco y la fecha se queda escrita en el idioma de la
  // sociedad anterior, que es la misma clase de error que el componente evita.
  useI18n()
  const legible = formatDateLegible(value)

  return (
    <div className="space-y-0.5">
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        min={min}
        max={max}
        aria-label={ariaLabel}
        /*
          `title` ademas del eco: el eco resuelve la lectura, pero quien navega
          con lector de pantalla o con el campo enfocado tambien tiene que poder
          saber que fecha es sin depender de un texto que esta al lado.
        */
        title={legible || undefined}
        className={cn("font-mono", className)}
      />
      {/*
        Sin fecha NO se reserva el hueco con un espacio en blanco: un campo
        vacio no tiene nada que desambiguar, y una linea vacia debajo se lee
        como algo que fallo al cargar.
      */}
      {legible ? (
        <div className="text-[0.65rem] leading-3 text-muted-foreground" aria-hidden="true">
          {legible}
        </div>
      ) : null}
    </div>
  )
}
